import React, { useState } from "react";
import { PaystackButton } from "react-paystack";
import { FaHeart, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { toast } from "react-toastify";
import styles from "./DonationPayment.module.css";

// Extend Window interface to include gtag
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

interface DonationPaymentProps {
  className?: string;
  amount: number; // Amount in kobo
  email: string;
  name?: string;
  anonymous?: boolean;
  onSuccess?: (transactionId: string) => void; // Added onSuccess prop
}

interface PaystackComponentProps {
  email: string;
  amount: number;
  publicKey: string;
  text: string;
  onSuccess: (reference: any) => void;
  onClose: () => void;
  metadata?: {
    custom_fields: Array<{
      display_name: string;
      variable_name: string;
      value: string;
    }>;
  };
}

const DonationPayment: React.FC<DonationPaymentProps> = ({
  className = "",
  amount,
  email,
  name = "",
  anonymous = false,
  onSuccess, // Added onSuccess prop
}) => {
  const [paymentStatus, setPaymentStatus] = useState<
    "idle" | "processing" | "success" | "cancelled"
  >("idle");

  // Validate email before proceeding
  const isValidEmail =
    email && email.includes("@") && email !== "donor@example.com";

  const formatAmount = (amountInKobo: number) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0,
    }).format(amountInKobo / 100);
  };

  const componentProps: PaystackComponentProps = {
    email: isValidEmail ? email : "donor@licomeds.com",
    amount: amount,
    publicKey:
      process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || "pk_test_your_key_here",
    text: "Donate Now",
    metadata: {
      custom_fields: [
        {
          display_name: "Donor Name",
          variable_name: "donor_name",
          value: anonymous ? "Anonymous Donor" : name || "Anonymous Donor",
        },
        {
          display_name: "Donation Type",
          variable_name: "donation_type",
          value: "Community Healthcare Support",
        },
        {
          display_name: "Platform",
          variable_name: "platform",
          value: "LicoMeds Website",
        },
      ],
    },
    onSuccess: (reference) => {
      console.log("Donation successful:", reference);
      setPaymentStatus("success");

      // Call the parent component's onSuccess callback if provided
      if (onSuccess) {
        onSuccess(
          reference.reference ||
            reference.trans ||
            reference.trxref ||
            "DON_" + Date.now()
        );
      }

      // Track successful donation with Google Analytics (if available)
      if (typeof window !== "undefined" && window.gtag) {
        window.gtag("event", "donation_completed", {
          event_category: "donation",
          event_label: "paystack",
          value: amount / 100,
        });
      }

      // Show success toast with custom styling
      toast.success(
        <div className={styles.toastContent}>
          <FaCheckCircle className={styles.toastIcon} />
          <div>
            <strong>Thank you for your generous donation!</strong>
            <p>
              Your contribution of {formatAmount(amount)} will help save lives.
              You will receive a confirmation email shortly.
            </p>
          </div>
        </div>,
        {
          position: "top-center",
          autoClose: 8000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          className: styles.successToast,
        }
      );

      // Optional: Save donation info to localStorage for thank you page
      localStorage.setItem(
        "lastDonation",
        JSON.stringify({
          amount: formatAmount(amount),
          reference: reference.reference,
          donorName: anonymous ? "Anonymous Donor" : name || "Anonymous Donor",
          timestamp: new Date().toISOString(),
        })
      );
    },
    onClose: () => {
      console.log("Payment window closed");
      setPaymentStatus("cancelled");

      // Track payment abandonment (if analytics available)
      if (typeof window !== "undefined" && window.gtag) {
        window.gtag("event", "donation_abandoned", {
          event_category: "donation",
          event_label: "paystack_closed",
        });
      }

      // Show info toast when user closes payment window
      toast.info(
        <div className={styles.toastContent}>
          <FaTimesCircle className={styles.toastIcon} />
          <div>
            <strong>Payment Cancelled</strong>
            <p>
              Your donation was not completed. You can try again anytime to
              support healthcare accessibility.
            </p>
          </div>
        </div>,
        {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          className: styles.infoToast,
        }
      );
    },
  };

  // Don't show button if email is not provided
  if (!isValidEmail) {
    return (
      <div className={styles.emailWarning}>
        <p>Please provide a valid email address to proceed with donation</p>
      </div>
    );
  }

  // Show success state if payment was successful
  if (paymentStatus === "success") {
    return (
      <div className={styles.successContainer}>
        <div className={styles.successIcon}>
          <FaCheckCircle />
        </div>
        <h3 className={styles.successTitle}>Donation Successful!</h3>
        <p className={styles.successMessage}>
          Thank you for your generous contribution of{" "}
          <strong>{formatAmount(amount)}</strong>
        </p>
        <p className={styles.successSubtext}>
          Your donation will help save lives. A confirmation email has been sent
          to you.
        </p>
        <div className={styles.successDetails}>
          <p>
            <strong>Donor:</strong>{" "}
            {anonymous ? "Anonymous" : name || "Anonymous"}
          </p>
          <p>
            <strong>Amount:</strong> {formatAmount(amount)}
          </p>
          <p>
            <strong>Status:</strong>{" "}
            <span className={styles.statusSuccess}>Completed</span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.paymentContainer}>
      <PaystackButton
        {...componentProps}
        className={`${styles.donationButton} ${className}`}
      >
        <FaHeart className={styles.buttonIcon} />
        <span>Donate Now</span>
      </PaystackButton>

      {paymentStatus !== "cancelled" && (
        <div className={styles.donationInfo}>
          <p className={styles.amountDisplay}>
            Donating: <strong>{formatAmount(amount)}</strong>
          </p>
          <p className={styles.securityText}>🔒 Secure payment via Paystack</p>
        </div>
      )}
    </div>
  );
};

export default DonationPayment;
